import { ReactNode, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PiArrowRight,
  PiBookOpenText,
  PiBookmarksSimple,
  PiCamera,
  PiClipboardText,
  PiHandTap,
  PiPlusBold,
  PiSparkle,
  PiSpeakerHigh,
} from "react-icons/pi";
import { useStoryListQuery } from "./queries";
import { lstr } from "./localization";
import { Settings } from "./settings";
import { LanguageDropdown } from "./LanguageDropdown";
import { GENDER_CLASS } from "./gender";
import { ExplanationText } from "./explanationMarkup";
import {
  ContinueReadingCard,
  FeaturedStoryCard,
  StoryLibrary,
  isFinished,
  storyOfTheDay,
} from "./StoryList";
import {
  isInProgress,
  loadReadingEntries,
  readingProgressByStoryId,
} from "./readingProgress";
import { SkeletonBlock, SkeletonStoryRows } from "./Skeleton";
import { Pup } from "./Pup";

// HomePage is the logged-out landing page mounted at "/" (logged-in users
// get StoryMenu there). It only relies on what already works anonymously:
// the built-in story list, the reader with word explanations, and links.
//
// The page is ordered to deliver the "aha" before asking for anything: the
// value proposition with the language pair and a way straight into a story,
// a preview of tapping a word, today's story and the freely readable
// library (the actual product demo), and only after that the signup-gated
// creation features and the signup invitation. Every gated action routes to
// /signup rather than /login because a first-time visitor almost never has
// an account yet.
export function HomePage({
  settings,
  setSettings,
}: {
  settings: Settings;
  setSettings: (value: Settings) => void;
}) {
  const l = settings.lLocale;
  const r = settings.rLocale;
  const strings = lstr(l);
  const navigate = useNavigate();
  const query = useStoryListQuery(l, r);
  const libraryRef = useRef<HTMLDivElement>(null);
  // Read once per visit of the page: progress only changes while reading.
  const readingEntries = useMemo(loadReadingEntries, []);
  const progressByStoryId = useMemo(
    () => readingProgressByStoryId(readingEntries),
    [readingEntries],
  );
  const continueEntry = readingEntries.find(
    (entry) => entry.r === r && isInProgress(entry.progress),
  );
  const featured = query.data
    ? storyOfTheDay(query.data, r, progressByStoryId)
    : undefined;

  const startReading = () => {
    if (continueEntry) navigate(`/${continueEntry.storyId}`);
    else if (featured) navigate(`/${featured.id}`);
    else libraryRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="w-full pb-16">
      <header className="relative flex items-start gap-6 pt-2">
        <div className="min-w-0 flex-1">
          <h1 className="font-literata text-[34px] font-bold leading-[1.1] tracking-tight text-main-text [text-wrap:balance] sm:text-[44px]">
            {strings.home_hero_title_pre}{" "}
            <span className="text-primary">
              {strings.home_hero_title_highlight}
            </span>
          </h1>
          <p className="mt-4 text-[17px] leading-relaxed text-secondary-text">
            {strings.home_hero_subtitle}
          </p>
        </div>
        <Pup
          shape="circle"
          className="hidden h-28 w-28 shrink-0 rotate-3 sm:block"
        />
      </header>

      <div className="mt-6 flex flex-wrap items-end gap-x-4 gap-y-3">
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow">{strings.language_i_speak}</span>
          <LanguageDropdown
            language={l}
            excludeLanguage={undefined}
            setLanguage={(value: string) =>
              setSettings({ ...settings, lLocale: value })
            }
          />
        </label>
        <PiArrowRight
          size={18}
          aria-hidden="true"
          className="mb-3 hidden text-secondary-text min-[400px]:block"
        />
        <label className="flex flex-col gap-1.5">
          <span className="eyebrow">{strings.language_i_learn}</span>
          <LanguageDropdown
            language={r}
            excludeLanguage={l}
            setLanguage={(value: string) =>
              setSettings({ ...settings, rLocale: value })
            }
          />
        </label>
      </div>

      <div className="mt-7 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={startReading}
          className="btn-primary px-6 py-3.5 text-[16px]"
        >
          <PiBookOpenText size={20} />
          {continueEntry
            ? strings.continue_reading
            : strings.home_start_reading}
        </button>
        <button
          type="button"
          onClick={() =>
            libraryRef.current?.scrollIntoView({ behavior: "smooth" })
          }
          className="btn-secondary px-5 py-3.5 text-[16px]"
        >
          {strings.home_browse_library}
        </button>
      </div>

      <TapWordPreview l={l} r={r} />

      <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-3">
        <HowItWorksStep
          icon={<PiBookOpenText size={20} />}
          title={strings.home_how_read_title}
          description={strings.home_how_read_description}
        />
        <HowItWorksStep
          icon={<PiHandTap size={20} />}
          title={strings.home_how_tap_title}
          description={strings.home_how_tap_description}
        />
        <HowItWorksStep
          icon={<PiBookmarksSimple size={20} />}
          title={strings.home_how_save_title}
          description={strings.home_how_save_description}
        />
      </div>

      {(continueEntry || featured) && (
        <div className="mt-12 grid gap-3">
          {continueEntry && <ContinueReadingCard l={l} entry={continueEntry} />}
          {featured && (
            <FeaturedStoryCard
              story={featured}
              l={l}
              r={r}
              eyebrow={strings.story_of_the_day}
              finished={isFinished(featured, progressByStoryId)}
            />
          )}
        </div>
      )}

      <div ref={libraryRef} className="mt-12 scroll-mt-20">
        {query.isPending && (
          <>
            <SkeletonBlock className="mb-4 h-7 w-32" />
            <SkeletonStoryRows />
          </>
        )}
        {query.isError && (
          <div className="card px-6 py-8 text-center text-secondary-text">
            {strings.loading_story_list_error}
          </div>
        )}
        {query.isSuccess && (
          <StoryLibrary
            l={l}
            r={r}
            stories={query.data}
            progressByStoryId={progressByStoryId}
          />
        )}
      </div>

      <section className="mt-14">
        <h2 className="font-literata text-2xl font-bold text-main-text">
          {strings.home_create_heading}
        </h2>
        <p className="mt-1 text-sm text-secondary-text">
          {strings.home_create_subtitle}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <CreateOptionCard
            icon={<PiSparkle size={20} />}
            title={strings.home_create_generate_title}
            description={strings.home_create_generate_description}
          />
          <CreateOptionCard
            icon={<PiCamera size={20} />}
            title={strings.home_create_scan_title}
            description={strings.home_create_scan_description}
          />
          <CreateOptionCard
            icon={<PiClipboardText size={20} />}
            title={strings.home_create_upload_title}
            description={strings.home_create_upload_description}
          />
        </div>
      </section>

      <section className="relative mt-14 overflow-hidden rounded-3xl bg-primary-light px-6 py-9 text-center">
        <Pup shape="circle" mood="happy" className="mx-auto h-20 w-20" />
        <h2 className="mt-4 font-literata text-2xl font-bold text-main-text">
          {strings.home_cta_title}
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-sm text-secondary-text">
          {strings.home_cta_subtitle}
        </p>
        <Link to="/signup" className="btn-primary mt-5 px-7 py-3.5 text-[16px]">
          {strings.home_cta_button}
        </Link>
      </section>
    </div>
  );
}

function HowItWorksStep({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3 sm:flex-col sm:gap-2.5">
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"
      >
        {icon}
      </span>
      <div>
        <div className="font-semibold text-main-text">{title}</div>
        <div className="mt-0.5 text-sm leading-relaxed text-secondary-text">
          {description}
        </div>
      </div>
    </div>
  );
}

// A sample sentence per learned language, with the word whose explanation
// the preview shows open, and that explanation (in explanation markup, like
// the real ones) and the sentence translation in each spoken language.
const TAP_WORD_SAMPLES: Record<
  string,
  {
    before: string;
    word: string;
    after: string;
    // Noun gender color of the word, as the reader would show it.
    wordClass: string;
    explanation: Record<string, string>;
    translation: Record<string, string>;
  }
> = {
  de: {
    before: "Jeden Morgen wartet der kleine Hund vor der ",
    word: "Bäckerei",
    after: ".",
    wordClass: GENDER_CLASS.f,
    explanation: {
      en: "<r>die Bäckerei</r> - <l>bakery</l>: a shop where bread is baked and sold. Plural <r>die Bäckereien</r>.",
      ru: "<r>die Bäckerei</r> - <l>пекарня</l>: магазин, где пекут и продают хлеб. Мн. ч. <r>die Bäckereien</r>.",
    },
    translation: {
      en: "Every morning the little dog waits outside the bakery.",
      ru: "Каждое утро маленький пёс ждёт у пекарни.",
    },
  },
  en: {
    before: "Every morning the little dog waits outside the ",
    word: "bakery",
    after: ".",
    wordClass: "",
    explanation: {
      de: "<r>bakery</r> - <l>die Bäckerei</l>: ein Geschäft, in dem Brot gebacken und verkauft wird. Plural <r>bakeries</r>.",
      ru: "<r>bakery</r> - <l>пекарня</l>: магазин, где пекут и продают хлеб. Мн. ч. <r>bakeries</r>.",
    },
    translation: {
      de: "Jeden Morgen wartet der kleine Hund vor der Bäckerei.",
      ru: "Каждое утро маленький пёс ждёт у пекарни.",
    },
  },
  ru: {
    before: "Каждое утро маленький пёс ждёт у ",
    word: "пекарни",
    after: ".",
    wordClass: GENDER_CLASS.f,
    explanation: {
      en: "<r>пекарни</r> - genitive of <r>пекарня</r> (<l>bakery</l>), which <r>у</r> (<l>outside</l>) requires.",
      de: "<r>пекарни</r> - Genitiv von <r>пекарня</r> (<l>Bäckerei</l>), den <r>у</r> (<l>vor</l>) verlangt.",
    },
    translation: {
      en: "Every morning the little dog waits outside the bakery.",
      de: "Jeden Morgen wartet der kleine Hund vor der Bäckerei.",
    },
  },
};

// TapWordPreview shows what the reader does with one tap: a sentence with a
// word's explanation open beneath it, styled like the real reader. It is a
// picture of the feature, so its buttons are decorative.
function TapWordPreview({ l, r }: { l: string; r: string }) {
  const strings = lstr(l);
  const sample = TAP_WORD_SAMPLES[r];
  const explanation = sample?.explanation[l];
  const translation = sample?.translation[l];
  if (!sample || !explanation || !translation) return null;
  return (
    <figure className="relative mt-12">
      <div className="card relative overflow-hidden px-5 pb-6 pt-5 sm:px-7">
        <div className="eyebrow mb-3 flex items-center gap-1.5 text-primary">
          <PiHandTap size={14} />
          {strings.home_preview_label}
        </div>
        <p
          lang={r}
          className="font-literata text-[19px] leading-[1.7] text-main-text sm:text-[21px]"
        >
          {sample.before}
          <span className="relative">
            <span className="rounded-[4px] bg-highlight px-[1px] text-on-highlight">
              {sample.word}
            </span>
          </span>
          {sample.after}
        </p>
        <div
          aria-hidden="true"
          className="relative mt-3 rounded-2xl border border-border bg-surface px-4 pb-4 pt-3.5 shadow-pop dark:border-white/15 dark:bg-[#2b2722] sm:ml-auto sm:max-w-sm"
        >
          <div
            lang={r}
            className={`font-literata text-xl font-bold leading-tight ${sample.wordClass || "text-main-text"}`}
          >
            {sample.word}
          </div>
          <div className="mb-3 mt-2.5 flex items-center gap-2">
            <span className="btn h-8 rounded-full bg-primary-light px-3 text-sm text-primary">
              <PiSpeakerHigh size={16} />
              {strings.listen_button}
            </span>
            <span className="btn-primary ml-auto h-8 rounded-full px-3.5 text-sm">
              <PiPlusBold size={12} />
              {strings.save_word_button}
            </span>
          </div>
          <p lang={l} className="text-[15px] leading-relaxed text-main-text">
            <ExplanationText markup={explanation} r={r} />
          </p>
        </div>
        <p
          lang={l}
          className="mt-4 border-l-2 border-primary/25 pl-3.5 font-literata text-[16px] leading-[1.65] text-secondary-text"
        >
          {translation}
        </p>
      </div>
    </figure>
  );
}

function CreateOptionCard({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Link
      to="/signup"
      className="card-interactive flex items-start gap-3 p-4 text-left sm:flex-col"
    >
      <span
        aria-hidden="true"
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"
      >
        {icon}
      </span>
      <div>
        <div className="font-semibold text-main-text">{title}</div>
        <div className="mt-0.5 text-sm leading-relaxed text-secondary-text">
          {description}
        </div>
      </div>
    </Link>
  );
}
